import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
interface Testimonial {
  initials: string;
  name: string;
  location: string;
  text: string;
  color: string;
  pack?: string;
}
interface TestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  className?: string;
}
export function Testimonials({
  testimonials,
  title,
  className = ''
}: TestimonialsProps) {
  return <section className={`py-16 bg-zinc-900 ${className}`} data-id="element-308">
      <div className="container mx-auto px-4" data-id="element-309">
        {title && <h2 className="font-bold text-center mb-12 text-white text-[16px] md:text-2xl" data-id="element-310">
            {title}
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-311">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-black/70 p-6 rounded-2xl shadow-lg border border-amber-900/40 flex flex-col h-full" data-id="element-312">
              <div className="flex items-center gap-1 mb-4" data-id="element-313">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" data-id="element-314" />)}
              </div>

              <p className="text-zinc-100 italic mb-6 flex-grow" data-id="element-315">"{t.text}"</p>

              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-amber-900/30" data-id="element-316">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${t.color}`} data-id="element-317">
                  {t.initials}
                </div>
                <div data-id="element-318">
                  <p className="font-bold text-white text-sm" data-id="element-319">{t.name}</p>
                  <p className="text-xs text-amber-200" data-id="element-320">{t.location}</p>
                  {t.pack && <p className="text-xs font-medium text-amber-300 mt-0.5" data-id="element-321">
                      {t.pack}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export const initialTestimonials = [{
  initials: 'MC',
  name: 'Matías C.',
  location: 'Apostador recreativo · Córdoba',
  text: 'Llevaba años apostando por intuición y perdiendo de forma consistente. Leí el Tomo 1 y el Tomo 3 antes del primer partido y por primera vez en mi vida entendí por qué estaba perdiendo. No era mala suerte. Era que no tenía método.',
  color: 'bg-amber-600'
}, {
  initials: 'RL',
  name: 'Rodrigo L.',
  location: 'Apostador intermedio · Rosario',
  text: 'El sistema de 6 capas me cambió completamente. Antes elegía el mercado por costumbre — siempre el 1X2. Ahora analizo el Over/Under, los corners, el BTTS. En la fase de grupos del torneo pasado, mis apuestas en mercados secundarios fueron las más rentables.',
  color: 'bg-yellow-600'
}, {
  initials: 'FV',
  name: 'Federico V.',
  location: 'Fanático del fútbol · Mendoza',
  text: 'Soy fanático del fútbol desde chico pero nunca entendí cómo funcionaban las cuotas de verdad. El Tomo 1 me explicó la probabilidad implícita, el margen del bookmaker y el concepto de value en un lenguaje que entendí a la primera. Ahora sé exactamente qué estoy haciendo cuando apuesto.',
  color: 'bg-orange-600'
}];
export const moreTestimonials = [{
  initials: 'JM',
  name: 'Juan M.',
  location: '🏙️ Buenos Aires',
  pack: 'Masterpack Apuestas Mundial',
  text: 'Antes del Mundial pasado aposté y terminé con menos de la mitad de lo que empecé. Este sistema me hizo entender que el problema no era mi conocimiento del fútbol — era que no tenía gestión de bankroll, apostaba en cada partido y seguía picks sin entender nada. Ahora tengo un método.',
  color: 'bg-emerald-600'
}, {
  initials: 'DS',
  name: 'Diego S.',
  location: '🏙️ Santa Fe',
  pack: 'Masterpack Apuestas Mundial',
  text: 'El Tomo 3 me cambió completamente cómo veo el fútbol. Antes miraba el partido y veía el resultado. Ahora veo el xG, el estilo táctico, la motivación, el contexto del torneo. El análisis de 6 capas suena complicado pero se hace en 20 minutos y es la diferencia entre apostar con criterio o apostar a ciegas.',
  color: 'bg-amber-700'
}, {
  initials: 'CP',
  name: 'Carlos P.',
  location: '🏙️ Tucumán',
  pack: 'Masterpack + Elite System',
  text: 'El Elite System me dio el modelo de Poisson que siempre quise entender. Ahora calculo la probabilidad de resultados exactos en 5 minutos con la calculadora del Tracker. No es garantía de ganar cada apuesta — pero sí es garantía de que cada apuesta tiene una base matemática real.',
  color: 'bg-yellow-700'
}, {
  initials: 'AR',
  name: 'Alejandro R.',
  location: '🏙️ La Plata',
  pack: 'Masterpack Apuestas Mundial',
  text: 'El Tomo 5 sobre gestión del bankroll debería ser obligatorio para cualquiera que apuesta. Aprendí qué era el tilt cuando ya había quemado la mitad de mi capital en 2 horas persiguiendo pérdidas. Con el protocolo anti-tilt del sistema eso no me pasó más.',
  color: 'bg-zinc-700'
}, {
  initials: 'MG',
  name: 'Marcelo G.',
  location: '🏙️ Rosario',
  pack: 'Masterpack + Elite System',
  text: 'Soy contador y pensé que entender las probabilidades de las apuestas iba a ser fácil para mí. Me equivoqué — el mercado de apuestas tiene su propia lógica. El Elite System me enseñó el modelo de Poisson, el clustering táctico y los sesgos cognitivos del apostador. Ahora analizo partidos como analizo balances.',
  color: 'bg-orange-700'
}];